import { useState } from "react";
import { Link } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { Copy, Loader2, RefreshCw } from "lucide-react";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { createUser } from "@/services/adminApi";
import type {
  AdminUserCreate,
  AdminUserSubscription,
} from "@/services/adminApi";
import { ApiError } from "@/services/api";

const TIERS = ["recon", "operator", "analyst"] as const;
const STATUSES = ["none", "active", "past_due", "canceled"] as const;
const MIN_PASSWORD_LENGTH = 8;
const GENERATED_PASSWORD_LENGTH = 20;
const PASSWORD_CHARSET =
  "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*";

interface CreateUserFormState {
  email: string;
  password: string;
  name: string;
  isBetaTester: boolean;
  subscriptionTier: string;
  subscriptionStatus: string;
  serialLookupMonthlyLimit: string;
  serialLookupUnlimited: boolean;
}

const defaultForm: CreateUserFormState = {
  email: "",
  password: "",
  name: "",
  isBetaTester: true,
  subscriptionTier: "recon",
  subscriptionStatus: "none",
  serialLookupMonthlyLimit: "",
  serialLookupUnlimited: false,
};

function generatePassword(): string {
  const bytes = new Uint32Array(GENERATED_PASSWORD_LENGTH);
  crypto.getRandomValues(bytes);
  return Array.from(
    bytes,
    (byte) => PASSWORD_CHARSET[byte % PASSWORD_CHARSET.length],
  ).join("");
}

function payloadFromForm(form: CreateUserFormState): AdminUserCreate {
  const trimmedLimit = form.serialLookupMonthlyLimit.trim();
  return {
    email: form.email.trim(),
    password: form.password,
    name: form.name.trim() || null,
    is_beta_tester: form.isBetaTester,
    subscription_tier: form.subscriptionTier,
    subscription_status: form.subscriptionStatus,
    serial_lookup_monthly_limit:
      trimmedLimit === "" ? null : Number(trimmedLimit),
    serial_lookup_unlimited: form.serialLookupUnlimited,
  };
}

function handleApiError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    toast.error(error.message);
    return;
  }
  toast.error(error instanceof Error ? error.message : fallback);
}

async function copyToClipboard(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  } catch {
    toast.error(`Failed to copy ${label.toLowerCase()}`);
  }
}

export default function CreateUser() {
  const [form, setForm] = useState<CreateUserFormState>(defaultForm);
  const [submittedPassword, setSubmittedPassword] = useState<string | null>(
    null,
  );
  const [createdUser, setCreatedUser] = useState<AdminUserSubscription | null>(
    null,
  );

  const createUserMutation = useMutation({
    mutationFn: (body: AdminUserCreate) => createUser(body),
  });

  const submitDisabled =
    !form.email.trim() ||
    form.password.length < MIN_PASSWORD_LENGTH ||
    createUserMutation.isPending;

  async function handleSubmit() {
    try {
      const payload = payloadFromForm(form);
      const created = await createUserMutation.mutateAsync(payload);
      setSubmittedPassword(payload.password);
      setCreatedUser(created);
      toast.success(`Created account for ${created.email}.`);
      setForm(defaultForm);
    } catch (error) {
      handleApiError(error, "Failed to create user.");
    }
  }

  function handleGeneratePassword() {
    setForm((prev) => ({ ...prev, password: generatePassword() }));
  }

  function handleCreateAnother() {
    setCreatedUser(null);
    setSubmittedPassword(null);
  }

  if (createdUser && submittedPassword) {
    return (
      <>
        <Toaster position="top-right" theme="dark" />
        <div className="max-w-xl space-y-6">
          <div>
            <h2 className="text-xl font-semibold">Create User</h2>
            <p className="text-sm text-muted-foreground">
              Create a test or dev customer account. No email is sent.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Account created</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <CredentialRow
                label="Email"
                value={createdUser.email ?? ""}
                onCopy={() => copyToClipboard(createdUser.email ?? "", "Email")}
              />
              <CredentialRow
                label="Password"
                value={submittedPassword}
                onCopy={() => copyToClipboard(submittedPassword, "Password")}
              />
              <p className="text-xs text-muted-foreground">
                The password is only shown here, once. It is not recoverable
                from the account list.
              </p>
              <div className="flex gap-2 pt-2">
                <Button asChild variant="outline">
                  <Link to="/users/subscriptions">Go to Subscriptions</Link>
                </Button>
                <Button onClick={handleCreateAnother}>Create another</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-xl space-y-6">
        <div>
          <h2 className="text-xl font-semibold">Create User</h2>
          <p className="text-sm text-muted-foreground">
            Create a test or dev customer account. No email is sent, and the
            account is never an admin.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="create-user-email">Email</Label>
              <Input
                id="create-user-email"
                type="email"
                value={form.email}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, email: event.target.value }))
                }
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="create-user-password">Password</Label>
              <div className="flex gap-2">
                <Input
                  id="create-user-password"
                  value={form.password}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      password: event.target.value,
                    }))
                  }
                />
                <Button
                  type="button"
                  variant="outline"
                  title="Generate password"
                  onClick={handleGeneratePassword}
                >
                  <RefreshCw className="size-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  title="Copy password"
                  disabled={!form.password}
                  onClick={() => copyToClipboard(form.password, "Password")}
                >
                  <Copy className="size-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                At least {MIN_PASSWORD_LENGTH} characters.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="create-user-name">Name (optional)</Label>
              <Input
                id="create-user-name"
                value={form.name}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, name: event.target.value }))
                }
              />
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Switch
                id="create-user-beta-tester"
                checked={form.isBetaTester}
                onCheckedChange={(checked) =>
                  setForm((prev) => ({ ...prev, isBetaTester: checked }))
                }
              />
              <Label htmlFor="create-user-beta-tester">Beta tester</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Beta testers bypass the site gate. Uncheck to test the
              gate-blocked login path.
            </p>
          </CardContent>
        </Card>

        <Collapsible defaultOpen={false}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer select-none">
                <CardTitle className="text-base">Subscription</CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Subscription tier</Label>
                    <Select
                      value={form.subscriptionTier}
                      onValueChange={(value) =>
                        setForm((prev) => ({
                          ...prev,
                          subscriptionTier: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIERS.map((tier) => (
                          <SelectItem key={tier} value={tier}>
                            {tier}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Subscription status</Label>
                    <Select
                      value={form.subscriptionStatus}
                      onValueChange={(value) =>
                        setForm((prev) => ({
                          ...prev,
                          subscriptionStatus: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="create-user-serial-limit">
                    Serial lookup monthly limit
                  </Label>
                  <Input
                    id="create-user-serial-limit"
                    type="number"
                    min={0}
                    placeholder="Default"
                    value={form.serialLookupMonthlyLimit}
                    disabled={form.serialLookupUnlimited}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        serialLookupMonthlyLimit: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="create-user-serial-unlimited"
                    checked={form.serialLookupUnlimited}
                    onCheckedChange={(checked) =>
                      setForm((prev) => ({
                        ...prev,
                        serialLookupUnlimited: checked,
                      }))
                    }
                  />
                  <Label htmlFor="create-user-serial-unlimited">
                    Unlimited serial lookups
                  </Label>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        <Button onClick={handleSubmit} disabled={submitDisabled}>
          {createUserMutation.isPending && (
            <Loader2 className="size-4 animate-spin" />
          )}
          Create User
        </Button>
      </div>
    </>
  );
}

function CredentialRow({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: () => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input readOnly value={value} className="font-mono text-sm" />
        <Button
          type="button"
          variant="outline"
          title={`Copy ${label}`}
          onClick={onCopy}
        >
          <Copy className="size-4" />
        </Button>
      </div>
    </div>
  );
}
